import { Bullseye, Button, Grid, GridItem, ToggleGroup, ToggleGroupItem } from "@patternfly/react-core";
import { Modal, ModalVariant } from "@patternfly/react-core/deprecated";
import { FormContainer } from "@rhc-shared-components/form";
import { FormRadio } from "@rhc-shared-components/form-radio-select";
import { FormTextInput } from "@rhc-shared-components/form-text-input";
import { Formik, type FormikProps } from "formik";
import { type FC, useEffect, useRef, useState } from "react";
import { ProjectContainerTypes, ProjectRedhatKeys, ProjectTypes } from "../../../constants/project";
import { useProductMetadata } from "../../../hooks/useProductMetadata";
import { useVendorDetails } from "../../../queries/productDetails";
import { DeployedAsSetType, getAttachedProjectBadgeSet, getDeployedAsSet } from "../../../utils/productDeployedAsSet";
import { AttachExistingComponents } from "./AttachExistingComponents";
import { initialModalValue, useCreateModalStore } from "./CreateModalStore";
import {
	ComponentCreateInitialValues,
	ComponentCreateValidator,
	ComponentTypes,
	ContainerStackComponentTypes,
	CreateFormKeys,
	EOSContentTypes,
	OpenStackVersions,
	ProjectContainerKeys,
	ProjectCreateModalKeys,
	ProjectDistributionMethodName,
	useCreateAndAttachComponent,
} from "./createConstants";
import { ContainerCreateForm } from "./Forms/ContainerCreateForm";
import { HelmChartCreateForm } from "./Forms/HelmChartCreateForm";
import { OperatorsCreateForm } from "./Forms/OperatorsCreateForm";
import { RhelContainerCreateForm } from "./Forms/RhelContainerCreateForm";

interface OpenshiftProductComponentModalProps {
	handleCreateModal: (showModal: boolean) => void;
	hideValidatedComponents?: boolean;
}

export const ContainerStackComponentModal: FC<OpenshiftProductComponentModalProps> = (
	props: OpenshiftProductComponentModalProps,
) => {
	const { certificationListState } = useProductMetadata();
	const vendorDetails = useVendorDetails();
	const step1Ref = useRef<FormikProps<Record<string, string>>>(null);
	const step2Ref = useRef<FormikProps<Record<string, string>>>(null);
	const attachRef = useRef<FormikProps<Record<string, string>>>(null);
	const [modalType, setModalType] = useState<"new" | "existing">("new");
	const { createNewComponent, isAttachComponentAPILoading, isCreateComponentAPILoading, isProductDetailsLoading } =
		useCreateAndAttachComponent(props.handleCreateModal, "new");
	const { setModalData, modalData, resetModalData } = useCreateModalStore();
	const handleModalTypeChange = (_selected: any, event: any) => {
		const newStatus = event.currentTarget.id;
		setModalType(newStatus);
	};
	const renderChildCreateForm = () => {
		switch (modalData.componentType as ComponentTypes) {
			case ComponentTypes.Containers:
				return <ContainerCreateForm handleCreateModal={props.handleCreateModal} ref={step2Ref} />;
			case ComponentTypes.OperatorBundle:
				return <OperatorsCreateForm handleCreateModal={props.handleCreateModal} ref={step2Ref} />;
			case ComponentTypes.HelmChart:
				return <HelmChartCreateForm handleCreateModal={props.handleCreateModal} ref={step2Ref} />;
			case ComponentTypes.RHEL:
				return <RhelContainerCreateForm handleCreateModal={props.handleCreateModal} ref={step2Ref} />;
		}
	};

	const getAttachedComponentTypes = () => {
		let allowedComponentTypes = ContainerStackComponentTypes;
		const attachComponentTypes = getDeployedAsSet(certificationListState || []);
		if (attachComponentTypes.has(DeployedAsSetType.operator) || attachComponentTypes.has(DeployedAsSetType.helmChart)) {
			allowedComponentTypes = allowedComponentTypes.filter(
				(item) => !(item.value === ComponentTypes.RHEL || item.value === ComponentTypes.openStackAppContainer),
			);
		} else if (
			attachComponentTypes.has(DeployedAsSetType.rhel) ||
			attachComponentTypes.has(DeployedAsSetType.openstackAppContainer)
		) {
			allowedComponentTypes = allowedComponentTypes.filter(
				(item) => !(item.value === ComponentTypes.OperatorBundle || item.value === ComponentTypes.HelmChart),
			);
		}

		// remove CNF if cni or csi is attached
		// https://issues.redhat.com/browse/CONNCERT-2448
		const allBadges = getAttachedProjectBadgeSet(certificationListState);
		if (allBadges.has("cni") || allBadges.has("csi")) {
			allowedComponentTypes = allowedComponentTypes.filter((item) => !(item.value === ComponentTypes.OpenShiftCNF));
		}
		return allowedComponentTypes;
	};

	useEffect(() => {
		document.body.classList.add("stop-scrolling");
		return () => {
			resetModalData();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resetModalData]);

	return (
		<>
			<Modal
				title="Add Component"
				variant={ModalVariant.medium}
				id="add-component-modal"
				className="add-component-modal"
				appendTo={document.getElementById("modal-div-container") ?? document.body}
				isOpen
				onClose={() => {
					props.handleCreateModal(false);
					document.body.classList.remove("stop-scrolling");
				}}
				style={modalData.pageChange ? { display: "none" } : { display: "block" }}
				actions={[
					<Button
						key="create"
						variant="primary"
						isLoading={isProductDetailsLoading || isCreateComponentAPILoading || isAttachComponentAPILoading}
						isDisabled={isProductDetailsLoading || isCreateComponentAPILoading || isAttachComponentAPILoading}
						{...(modalType === "new"
							? [
									ComponentTypes.OpenShiftCNF,
									ComponentTypes.openStackAppContainer,
									ComponentTypes.OpenShiftVirtualization,
								].includes(modalData.componentType)
								? {
										"data-analytics-category": "Product Info|Add Component",
									}
								: {}
							: { "data-analytics-category": "Product Info|Attach Existing Component" })}
						onClick={() => {
							modalType === "new" ? step1Ref?.current?.handleSubmit() : attachRef?.current?.handleSubmit();
						}}
					>
						{`${
							modalType === "new"
								? [
										ComponentTypes.OpenShiftCNF,
										ComponentTypes.openStackAppContainer,
										ComponentTypes.OpenShiftVirtualization,
									].includes(modalData.componentType)
									? "Create new component"
									: "Next"
								: "Attach existing component"
						}`}
					</Button>,
					<Button
						key="cancel"
						isDisabled={isProductDetailsLoading || isCreateComponentAPILoading || isAttachComponentAPILoading}
						variant="link"
						onClick={() => {
							props.handleCreateModal(false);
							document.body.classList.remove("stop-scrolling");
						}}
					>
						Cancel
					</Button>,
				]}
			>
				<Grid hasGutter>
					<GridItem>
						<Bullseye>
							<ToggleGroup>
								<ToggleGroupItem
									text="New Component"
									buttonId="new"
									isSelected={modalType === "new"}
									isDisabled={isProductDetailsLoading || isCreateComponentAPILoading || isAttachComponentAPILoading}
									onChange={(event, selected: any) => handleModalTypeChange(selected, event)}
								/>
								<ToggleGroupItem
									text="Existing Component"
									buttonId="existing"
									isSelected={modalType === "existing"}
									isDisabled={isProductDetailsLoading || isCreateComponentAPILoading || isAttachComponentAPILoading}
									onChange={(event, selected: any) => handleModalTypeChange(selected, event)}
								/>
							</ToggleGroup>
						</Bullseye>
					</GridItem>
					<GridItem>
						<div style={modalType === "existing" ? { display: "none" } : { display: "block" }}>
							<Formik
								innerRef={step1Ref}
								initialValues={ComponentCreateInitialValues}
								validationSchema={ComponentCreateValidator}
								onSubmit={async (values) => {
									if (values[CreateFormKeys.componentType] === ComponentTypes.OpenShiftCNF) {
										const requestData = {
											[ProjectCreateModalKeys.name]: values[CreateFormKeys.componentName],
											[ProjectCreateModalKeys.type]: ProjectTypes.openShiftCnf,
											[ProjectCreateModalKeys.projectStatus]: "active",
										};
										await createNewComponent(requestData);
									} else if (values[CreateFormKeys.componentType] === ComponentTypes.openStackAppContainer) {
										const requestData = {
											[ProjectCreateModalKeys.name]: values[CreateFormKeys.componentName],
											[ProjectCreateModalKeys.container]: {
												[ProjectContainerKeys.distributionMethod]:
													ProjectDistributionMethodName.redHatContainerRegistry,
												[ProjectContainerKeys.osContentType]: EOSContentTypes.RHEL,
												[ProjectContainerKeys.type]: ProjectContainerTypes.container,
												[ProjectCreateModalKeys.allowHostedRegistry]: vendorDetails?.allow_hosted_container_registry,
											},
											[ProjectCreateModalKeys.redhat]: {
												[ProjectRedhatKeys.productName]: "OpenStack",
												[ProjectRedhatKeys.productVersion]: values[ProjectRedhatKeys.productVersion],
											},
											[ProjectCreateModalKeys.marketplace]: {
												enablement_status: "ineligible",
											},
											[ProjectCreateModalKeys.type]: ComponentTypes.openStackAppContainer,
											[ProjectCreateModalKeys.projectStatus]: "active",
										};
										await createNewComponent(requestData);
									} else if (values[CreateFormKeys.componentType] === ComponentTypes.OpenShiftVirtualization) {
										const requestData = {
											[ProjectCreateModalKeys.name]: values[CreateFormKeys.componentName],
											[ProjectCreateModalKeys.type]: ProjectTypes.openShiftVirtualization,
											[ProjectCreateModalKeys.projectStatus]: "active",
										};
										await createNewComponent(requestData);
									} else {
										setModalData({
											pageChange: true,
											componentType: values[CreateFormKeys.componentType],
											componentName: values[CreateFormKeys.componentName],
										});
									}
								}}
							>
								{({ values }) => (
									<FormContainer gridSpan={12}>
										<FormTextInput
											name={CreateFormKeys.componentName}
											label={"Component Name"}
											isRequired
											helperText={"This is for internal use only and will not be visible to customers"}
										/>
										<FormRadio
											name={CreateFormKeys.componentType}
											label={"What kind of OpenShift component are you creating?"}
											isRequired={true}
											options={getAttachedComponentTypes()}
											onChange={(selectedValue: string) => {
												setModalData({
													componentType: selectedValue,
												});
											}}
											span={12}
										/>
										{values[CreateFormKeys.componentType] === ComponentTypes.openStackAppContainer && (
											<FormRadio
												name={CreateFormKeys.productVersion}
												label={`Red Hat OpenStack version`}
												isRequired={true}
												options={OpenStackVersions}
												subLabel={
													<div style={{ marginBottom: "0.5rem" }}>
														What major version of OpenStack will you be certifying your image for?{" "}
													</div>
												}
												span={12}
											/>
										)}
									</FormContainer>
								)}
							</Formik>
						</div>
						<div style={modalType === "new" ? { display: "none" } : { display: "block" }}>
							<AttachExistingComponents
								{...(props?.hideValidatedComponents !== undefined
									? { hideValidatedComponents: props.hideValidatedComponents }
									: {})}
								handleCreateModal={props.handleCreateModal}
								ref={attachRef}
							/>
						</div>
					</GridItem>
				</Grid>
			</Modal>
			{modalData.pageChange && (
				<Modal
					title={`Create and Add ${modalData.componentName}`}
					variant={ModalVariant.medium}
					id="add-component-form-modal"
					className="add-component-form-modal"
					appendTo={document.getElementById("modal-div-container") ?? document.body}
					isOpen
					onClose={() => {
						props.handleCreateModal(false);
						document.body.classList.remove("stop-scrolling");
					}}
					actions={[
						<Button
							key="create-form"
							data-analytics-category="Product Info|Add Component"
							variant="primary"
							isLoading={isProductDetailsLoading || isAttachComponentAPILoading || isCreateComponentAPILoading}
							isDisabled={isProductDetailsLoading || isAttachComponentAPILoading || isCreateComponentAPILoading}
							onClick={() => {
								step2Ref?.current?.handleSubmit();
							}}
						>
							Add Component
						</Button>,
						<Button
							key="back"
							variant="secondary"
							isDisabled={isAttachComponentAPILoading || isCreateComponentAPILoading}
							onClick={() => {
								setModalData(initialModalValue);
							}}
						>
							Back
						</Button>,
						<Button
							key="cancel-form"
							variant="link"
							isDisabled={isAttachComponentAPILoading || isCreateComponentAPILoading}
							onClick={() => {
								props.handleCreateModal(false);
								document.body.classList.remove("stop-scrolling");
							}}
						>
							Cancel
						</Button>,
					]}
				>
					{renderChildCreateForm()}
				</Modal>
			)}
		</>
	);
};
